import React, { useEffect, useState } from 'react';
import { Snackbar, Paper, Box, Typography, LinearProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface DownloadProgressSnackbarProps {
  open: boolean;
  progress?: number;
}

const DownloadProgressSnackbar: React.FC<DownloadProgressSnackbarProps> = ({ open, progress }) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(open);

  // Check if progress is complete (100%)
  useEffect(() => {
    if (progress === 100 && open) {
      setShowSuccess(true);
      // Show success message for 100ms, then close
      const timer = setTimeout(() => {
        setSnackbarOpen(false);
      }, 500);
      return () => clearTimeout(timer);
    } else if (progress !== 100) {
      setShowSuccess(false);
    }
  }, [progress, open]);

  // Sync snackbar open state with prop
  useEffect(() => {
    if (open && progress !== 100) {
      setSnackbarOpen(open);
    }
    if (!open) {
      setSnackbarOpen(false);
      setShowSuccess(false);
    }
  }, [open, progress]);

  const isComplete = progress === 100 && showSuccess;

  return (
    <Snackbar
      open={snackbarOpen}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      sx={{ mb: 1, mr: 1, zIndex: 10000 }}
    >
      <Paper
        elevation={6}
        sx={{
          width: 360,
          height: isComplete ? 55 : 60,
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {isComplete ? (
          <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 2, gap: 1 }}>
            <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 16 }} />
            <Typography variant="body1" fontWeight={400} fontSize={14}>
              Report downloaded!
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 2.2, gap: 1 }}>
              <img src="/images/info-circle-solid.svg" alt="info" width={16} height={16} />
              <Typography variant="body1" fontWeight={400} fontSize={14}>
                Downloading in progress
              </Typography>
            </Box>

            <LinearProgress
              variant={progress !== undefined ? 'determinate' : 'indeterminate'}
              value={progress}
            />
          </>
        )}
      </Paper>
    </Snackbar>
  );
};

export default DownloadProgressSnackbar;
